<template>
  <div class="card border-0 shadow-none rounded-1 mb-25">
    <div class="card-body p-xl-40">
      <div class="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-4">
        <ul class="nav nav-pills warehouse-report-tabs">
          <li class="nav-item" v-for="tab in tabs" :key="tab.key">
            <button
              type="button"
              class="nav-link"
              :class="{ active: activeTab === tab.key }"
              @click="$emit('tab-change', tab.key)"
            >
              {{ tab.label }}
            </button>
          </li>
        </ul>

        <span class="summary-chip">
          {{ summary.label }}: ${{ formatAmount(summary.value) }}
        </span>
      </div>

      <a-table
        :columns="currentColumns"
        :data-source="currentItems"
        :row-key="rowKey"
        :loading="loading"
        :pagination="pagination"
        :scroll="{ x: 1120 }"
        class="text-nowrap align-middle warehouse-report-table"
        @change="handleChange"
      >
        <template #emptyText>
          <div class="py-4 text-center text-muted">{{ emptyText }}</div>
        </template>

        <template #bodyCell="{ column, record }">
          <template v-if="column && column.key === 'date'">
            {{ formatDate(record.created_at) }}
          </template>

          <template v-else-if="column && column.key === 'status'">
            <span class="status-badge" :class="getStatusClass(record.status)">
              {{ formatLabel(record.status) }}
            </span>
          </template>

          <template v-else-if="column && column.key === 'invoice_total'">
            ${{ formatAmount(record.invoice_total) }}
          </template>

          <template v-else-if="column && column.key === 'paid'">
            ${{ formatAmount(record.paid) }}
          </template>

          <template v-else-if="column && column.key === 'due'">
            ${{ formatAmount(getDue(record)) }}
          </template>

          <template v-else-if="column && column.key === 'payment_status'">
            <span class="status-badge" :class="getPaymentStatusClass(record.payment_status || getPaymentStatus(record))">
              {{ formatLabel(record.payment_status || getPaymentStatus(record)) }}
            </span>
          </template>

          <template v-else-if="column && column.key === 'amount'">
            ${{ formatAmount(record.amount) }}
          </template>

          <template v-else-if="column && column.key === 'description'">
            {{ record.description || "-" }}
          </template>
        </template>
      </a-table>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent } from "vue";
import { useI18n } from "vue-i18n";
import type { TablePaginationConfig } from "ant-design-vue";
import type {
  WarehouseExpenseReportItem,
  WarehousePurchaseReportItem,
  WarehouseQuotationReportItem,
  WarehouseSaleReportItem,
} from "@/services/report";

type WarehouseReportTab = "sale" | "purchase" | "quotation" | "expense";

export default defineComponent({
  name: "WarehouseReport",
  props: {
    activeTab: {
      type: String as () => WarehouseReportTab,
      required: true,
    },
    sales: {
      type: Array as () => WarehouseSaleReportItem[],
      default: () => [],
    },
    purchases: {
      type: Array as () => WarehousePurchaseReportItem[],
      default: () => [],
    },
    quotations: {
      type: Array as () => WarehouseQuotationReportItem[],
      default: () => [],
    },
    expenses: {
      type: Array as () => WarehouseExpenseReportItem[],
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    pagination: {
      type: Object as () => TablePaginationConfig,
      required: true,
    },
    summary: {
      type: Object as () => { label: string; value: number },
      required: true,
    },
    i18nScope: {
      type: String,
      default: "warehouse_report_page",
    },
  },
  emits: ["tab-change", "table-change"],
  setup(props, { emit }) {
    const { t, locale } = useI18n();

    const tabs = computed<Array<{ key: WarehouseReportTab; label: string }>>(() => [
      { key: "sale", label: t(`${props.i18nScope}.tab_sales`) },
      { key: "purchase", label: t(`${props.i18nScope}.tab_purchases`) },
      { key: "quotation", label: t(`${props.i18nScope}.tab_quotations`) },
      { key: "expense", label: t(`${props.i18nScope}.tab_expenses`) },
    ]);

    const saleColumns = computed(() => [
      { title: t(`${props.i18nScope}.table_date`), key: "date", dataIndex: "created_at", width: 210, align: "center" as const },
      { title: t(`${props.i18nScope}.table_reference`), key: "reference_no", dataIndex: "reference_no", width: 160, align: "center" as const },
      { title: t(`${props.i18nScope}.table_customer`), key: "customer_name", dataIndex: "customer_name", width: 220, align: "center" as const },
      { title: t(`${props.i18nScope}.table_status`), key: "status", dataIndex: "status", width: 140, align: "center" as const },
      { title: t(`${props.i18nScope}.table_grand_total`), key: "invoice_total", dataIndex: "invoice_total", width: 150, align: "center" as const },
      { title: t(`${props.i18nScope}.table_paid`), key: "paid", dataIndex: "paid", width: 120, align: "center" as const },
      { title: t(`${props.i18nScope}.table_due`), key: "due", width: 120, align: "center" as const },
      { title: t(`${props.i18nScope}.table_payment_status`), key: "payment_status", width: 170, align: "center" as const },
    ]);

    const purchaseColumns = computed(() => [
      { title: t(`${props.i18nScope}.table_date`), key: "date", dataIndex: "created_at", width: 210, align: "center" as const },
      { title: t(`${props.i18nScope}.table_reference`), key: "reference_no", dataIndex: "reference_no", width: 160, align: "center" as const },
      { title: t(`${props.i18nScope}.table_supplier`), key: "supplier_name", dataIndex: "supplier_name", width: 220, align: "center" as const },
      { title: t(`${props.i18nScope}.table_status`), key: "status", dataIndex: "status", width: 140, align: "center" as const },
      { title: t(`${props.i18nScope}.table_grand_total`), key: "invoice_total", dataIndex: "invoice_total", width: 150, align: "center" as const },
      { title: t(`${props.i18nScope}.table_paid`), key: "paid", dataIndex: "paid", width: 120, align: "center" as const },
      { title: t(`${props.i18nScope}.table_due`), key: "due", width: 120, align: "center" as const },
      { title: t(`${props.i18nScope}.table_payment_status`), key: "payment_status", width: 170, align: "center" as const },
    ]);

    const quotationColumns = computed(() => [
      { title: t(`${props.i18nScope}.table_date`), key: "date", dataIndex: "created_at", width: 210, align: "center" as const },
      { title: t(`${props.i18nScope}.table_reference`), key: "reference_no", dataIndex: "reference_no", width: 160, align: "center" as const },
      { title: t(`${props.i18nScope}.table_customer`), key: "customer_name", dataIndex: "customer_name", width: 220, align: "center" as const },
      { title: t(`${props.i18nScope}.table_status`), key: "status", dataIndex: "status", width: 140, align: "center" as const },
      { title: t(`${props.i18nScope}.table_quantity`), key: "quantity", dataIndex: "quantity", width: 120, align: "center" as const },
      { title: t(`${props.i18nScope}.table_price`), key: "price", dataIndex: "price", width: 130, align: "center" as const },
      { title: t(`${props.i18nScope}.table_grand_total`), key: "invoice_total", dataIndex: "invoice_total", width: 150, align: "center" as const },
    ]);

    const expenseColumns = computed(() => [
      { title: t(`${props.i18nScope}.table_date`), key: "date", dataIndex: "created_at", width: 180, align: "center" as const },
      { title: t(`${props.i18nScope}.table_reference`), key: "reference_no", dataIndex: "reference_no", width: 150, align: "center" as const },
      { title: t(`${props.i18nScope}.table_category`), key: "category_name", dataIndex: "category_name", width: 180, align: "center" as const },
      { title: t(`${props.i18nScope}.table_expense_name`), key: "expense_name", dataIndex: "expense_name", width: 220, align: "center" as const },
      { title: t(`${props.i18nScope}.table_amount`), key: "amount", dataIndex: "amount", width: 140, align: "center" as const },
      { title: t(`${props.i18nScope}.table_status`), key: "status", dataIndex: "status", width: 120, align: "center" as const },
      { title: t(`${props.i18nScope}.table_description`), key: "description", dataIndex: "description", width: 250, align: "center" as const },
    ]);

    const currentColumns = computed(() => {
      if (props.activeTab === "purchase") return purchaseColumns.value;
      if (props.activeTab === "quotation") return quotationColumns.value;
      if (props.activeTab === "expense") return expenseColumns.value;
      return saleColumns.value;
    });

    const currentItems = computed(() => {
      if (props.activeTab === "purchase") return props.purchases;
      if (props.activeTab === "quotation") return props.quotations;
      if (props.activeTab === "expense") return props.expenses;
      return props.sales;
    });

    const rowKey = computed(() => {
      if (props.activeTab === "purchase") return "purchase_id";
      if (props.activeTab === "quotation") return "quote_id";
      if (props.activeTab === "expense") return "expense_id";
      return "sale_id";
    });

    const emptyText = computed(() => {
      if (props.activeTab === "purchase") return t(`${props.i18nScope}.no_purchases_found`);
      if (props.activeTab === "quotation") return t(`${props.i18nScope}.no_quotations_found`);
      if (props.activeTab === "expense") return t(`${props.i18nScope}.no_expenses_found`);
      return t(`${props.i18nScope}.no_sales_found`);
    });

    const formatDate = (dateString?: string) => {
      if (!dateString) return t(`${props.i18nScope}.not_available`);
      return new Date(dateString).toLocaleString(locale.value === "km" ? "km-KH" : "en-GB", {
        timeZone: "Asia/Phnom_Penh",
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: true,
      });
    };

    const formatAmount = (value?: string | number) =>
      Number(value ?? 0).toLocaleString(locale.value === "km" ? "km-KH" : "en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    const getDue = (record: { invoice_total?: string | number; paid?: string | number }) =>
      Number(record.invoice_total ?? 0) - Number(record.paid ?? 0);
    const getPaymentStatus = (record: { invoice_total?: string | number; paid?: string | number }) => {
      const due = getDue(record);
      const paid = Number(record.paid ?? 0);

      if (paid <= 0) return "unpaid";
      if (due <= 0) return "paid";
      return "partial";
    };
    const formatLabel = (value?: string | number) => {
      if (value === 1 || value === "1") return t(`${props.i18nScope}.status_active`);
      if (value === 0 || value === "0") return t(`${props.i18nScope}.status_inactive`);

      const text = String(value ?? "pending")
        .replace(/_/g, " ")
        .toLowerCase();
      const normalizedKey = text.replace(/\s+/g, "_");
      const translationKey = `${props.i18nScope}.status_${normalizedKey}`;
      const translated = t(translationKey);
      if (translated !== translationKey) return translated;

      return text.charAt(0).toUpperCase() + text.slice(1);
    };
    const getStatusClass = (value?: string | number) => {
      const normalized = String(value ?? "pending").toLowerCase();
      if (["received", "completed", "approved", "1", "active"].includes(normalized)) return "status-success";
      if (["pending", "ordered", "draft", "0", "inactive"].includes(normalized)) return "status-warning";
      if (["cancelled", "rejected"].includes(normalized)) return "status-danger";
      return "status-neutral";
    };
    const getPaymentStatusClass = (value?: string | number) => {
      const normalized = String(value ?? "pending").toLowerCase();
      if (normalized === "paid") return "status-success";
      if (normalized === "partial") return "status-warning";
      return "status-danger";
    };

    const handleChange = (pagination: TablePaginationConfig) => {
      emit("table-change", pagination.current || 1, pagination.pageSize || 10);
    };

    return {
      tabs,
      currentColumns,
      currentItems,
      rowKey,
      emptyText,
      formatDate,
      formatAmount,
      getDue,
      getPaymentStatus,
      formatLabel,
      getStatusClass,
      getPaymentStatusClass,
      handleChange,
    };
  },
});
</script>

<style scoped>
.warehouse-report-tabs {
  gap: 10px;
}

.warehouse-report-tabs .nav-link {
  border: 1px solid #e6ebf2;
  border-radius: 999px;
  padding: 10px 18px;
  font-size: 13px;
  font-weight: 600;
  color: #5b6780;
  background: #f8fafc;
}

.warehouse-report-tabs .nav-link.active {
  border-color: #5d5fef;
  background: rgba(93, 95, 239, 0.12);
  color: #5d5fef;
}

.summary-chip {
  display: inline-flex;
  align-items: center;
  padding: 10px 16px;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 600;
  color: #5d5fef;
  background: rgba(93, 95, 239, 0.1);
}

.warehouse-report-table :deep(.ant-table-cell) {
  vertical-align: middle;
}

.warehouse-report-table :deep(.ant-table-thead > tr > th) {
  white-space: nowrap;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 96px;
  padding: 6px 12px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
}

.status-success {
  color: #1f9254;
  background: #e8f7ee;
}

.status-warning {
  color: #d48a00;
  background: #fff4db;
}

.status-danger {
  color: #d33c43;
  background: #fdecec;
}

.status-neutral {
  color: #61748a;
  background: #eef2f7;
}
</style>
